import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { ComposeOutput, Draft, ID, Snapshot, Story } from '@proofroom/schema';
import { useDocument, useSnapshot } from '../api/queries';
import { drafts as draftApi } from '../api/endpoints';
import { indexSnapshot } from '../model/snapshotIndex';
import { storyGeometry } from '../model/frameGeometry';
import { fontLocks } from '../model/fontLock';
import { chooseOption, creativeOptionGroups, type LayerOverrides } from '../render/layers';
import { SpreadSvg } from '../render/SpreadSvg';
import { domMeasure } from '../render/measure';
import { registerSnapshotFaces, type FaceLoadResult } from '../fonts/faces';
import { createComposer } from '../engine/composer';
import { differingStories, sheetSize, spreadSelection } from '../print/sheet';

export const PREVIEW_CAVEAT = 'Proofroom preview, not final artwork. Fonts, colours and line breaks may differ from the printed document.';

const noop = () => {};

/**
 * The page the server prints in headless Chromium. No chrome of any kind: one sheet per requested
 * spread at its exact point size, and `body[data-print-ready]` once there is nothing left to draw.
 */
export function PrintPage() {
  const { documentId = '', draftId = '' } = useParams();
  const [search] = useSearchParams();
  const doc = useDocument(documentId);
  const snap = useSnapshot(draftId);
  // never refetched: a second answer mid print would move the lines under the server's feet
  const current = useQuery({
    queryKey: ['drafts', draftId, 'stories', 'current'],
    queryFn: () => draftApi.currentStories(draftId),
    staleTime: Infinity,
    gcTime: 10 * 60 * 1000,
  });

  if (doc.isPending || snap.isPending || current.isPending) return <div className="print" />;
  if (doc.isError) return <PrintProblem message={doc.error.message} />;
  if (snap.isError) return <PrintProblem message={snap.error.message} />;
  if (current.isError) return <PrintProblem message={current.error.message} />;
  const draft = doc.data.drafts.find((d) => d.id === draftId);
  if (!draft) return <PrintProblem message="This draft does not belong to the document" />;

  return <Sheets docName={doc.data.name} draft={draft} snapshot={snap.data} current={current.data} spreadsParam={search.get('spreads')} />;
}

/**
 * Never sets data-print-ready: a failed page must not print as a PDF. It says so on the body instead,
 * so the server answers 502 with the reason rather than waiting out its two minute timeout.
 */
function PrintProblem({ message }: { message: string }) {
  useEffect(() => {
    document.body.dataset.printError = message;
    return () => { delete document.body.dataset.printError; };
  }, [message]);
  return <div className="print"><p className="print-problem">Could not build this preview: {message}</p></div>;
}

interface SheetsProps { docName: string; draft: Draft; snapshot: Snapshot; current: Story[]; spreadsParam: string | null }

function Sheets({ docName, draft, snapshot, current, spreadsParam }: SheetsProps) {
  const idx = useMemo(() => indexSnapshot(snapshot), [snapshot]);
  const selection = useMemo(() => spreadSelection(spreadsParam, snapshot.spreads.length), [spreadsParam, snapshot.spreads.length]);

  // the creative options the snapshot itself shows, as the viewer opens them
  const layerOverrides = useMemo(() => {
    let o: LayerOverrides = {};
    for (const g of creativeOptionGroups(snapshot)) o = chooseOption(g, g.defaultLayerId, o);
    return o;
  }, [snapshot]);

  const [faces, setFaces] = useState<FaceLoadResult | null>(null);
  useEffect(() => {
    let alive = true;
    registerSnapshotFaces(snapshot.fonts).then((r) => { if (alive) setFaces(r); });
    return () => { alive = false; };
  }, [snapshot]);
  const fontsReady = faces !== null;
  const fontLockMap = useMemo(() => (faces ? fontLocks(idx, new Set(faces.loaded)) : {}), [idx, faces]);
  // render/lines.ts needs a measurer to place tab stops, dot leaders and justified words
  const measure = useMemo(() => (fontsReady ? domMeasure() : undefined), [fontsReady]);

  const edited = useMemo(() => differingStories(snapshot.stories, current), [snapshot.stories, current]);
  const [composed, setComposed] = useState<Record<ID, ComposeOutput> | null>(null);
  const [failed, setFailed] = useState<ID[]>([]);

  useEffect(() => {
    let alive = true;
    setComposed(null);
    const composer = createComposer();
    const fonts = snapshot.fonts.filter((f) => f.status !== 'missing');
    const out: Record<ID, ComposeOutput> = {};
    const errs: ID[] = [];
    void Promise.all(edited.map(async (story) => {
      const base = idx.stories[story.id];
      if (!base || base.hasTable) return;
      try {
        const { output } = await composer.compose({
          story,
          paragraphStyles: idx.paraStyles,
          characterStyles: idx.charStyles,
          fonts,
          frames: storyGeometry(base, idx),
          baselineGrid: snapshot.baselineGrid,
        });
        out[story.id] = output;
      } catch (err) {
        console.warn('[print] composition failed, printing InDesign lines instead', story.id, err);
        errs.push(story.id);
      }
    })).then(() => {
      if (!alive) return;
      setComposed(out);
      setFailed(errs);
    });
    return () => { alive = false; composer.dispose(); };
  }, [edited, idx, snapshot]);

  useEffect(() => { document.title = `PREVIEW ONLY: ${docName}`; }, [docName]);

  const ready = fontsReady && composed !== null;
  useEffect(() => {
    if (!ready) return;
    document.body.dataset.composeFailures = String(failed.length);
    document.body.dataset.printReady = '1';
    return () => { delete document.body.dataset.printReady; delete document.body.dataset.composeFailures; };
  }, [ready, failed.length]);

  const exportedAt = useMemo(() => new Date().toLocaleString('en-GB'), []);

  return (
    <div className="print">
      {selection.map((i) => {
        const spread = snapshot.spreads[i];
        const { w, h } = sheetSize(spread);
        const lost = failed.filter((id) => (idx.spreadStories[i] ?? []).includes(id)).length;
        return (
          <section key={spread.id} className="sheet" data-spread={i} data-w={w} data-h={h} style={{ width: `${w}pt`, height: `${h}pt` }}>
            <SpreadSvg
              idx={idx} spread={spread} scale={1} layerOverrides={layerOverrides} composed={composed ?? {}} overset={{}}
              selection={[]} threads={[]} showReference={false} referenceOpacity={0} showFrames={false} pinTool={false}
              measure={measure} textReady={fontsReady} fontLocks={fontLockMap}
              onFrameClick={noop} onPin={noop} onMarkerClick={noop}
            />
            <header className="sheet-strip">
              <span className="caveat">{PREVIEW_CAVEAT}</span>
              <span className="meta">{docName} · Draft {draft.number} · {exportedAt}</span>
            </header>
            <div className="sheet-watermark" style={{ fontSize: `${Math.round(w / 6)}pt` }} aria-hidden="true">PREVIEW</div>
            {lost > 0 && (
              <footer className="sheet-note">{lost} stor{lost === 1 ? 'y' : 'ies'} printed from InDesign&apos;s lines because composition failed</footer>
            )}
          </section>
        );
      })}
    </div>
  );
}
